import { Box, IconButton, Tooltip, Typography } from '@compass/core';
import AddIcon from '@mui/icons-material/Add';
import { alpha, useTheme } from '@mui/material/styles';
import {
    DashboardTabConfig,
    TabId,
    MAX_TABS,
} from '../types/DashboardTabConfig';

interface DashboardTabBarProps {
    tabs: DashboardTabConfig[];
    activeTabId: TabId;
    onSelectTab: (tabId: TabId) => void;
    onAddTab: () => void;
}

export const DashboardTabBar = ({
    tabs,
    activeTabId,
    onSelectTab,
    onAddTab,
}: DashboardTabBarProps) => {
    const theme = useTheme();
    const canAddTab = tabs.length < MAX_TABS;

    const handleTabKeyDown = (
        event: React.KeyboardEvent,
        tabId: TabId
    ) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelectTab(tabId);
        }
    };

    return (
        <Box
            role="tablist"
            aria-label="Dashboard tabs"
            sx={{
                display: 'flex',
                alignItems: 'center',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                gap: 1,
                pb: 1,
            }}
        >
            {tabs.map((tab) => {
                const isActive = tab.id === activeTabId;
                const currentColorKey = tab.colorKey;
                const tabColor = theme.palette.dashboardTabs[currentColorKey];
                return (
                    <Box
                        key={tab.id}
                        onClick={() => onSelectTab(tab.id)}
                        onKeyDown={(e) => handleTabKeyDown(e, tab.id)}
                        role="tab"
                        aria-selected={isActive}
                        tabIndex={isActive ? 0 : -1}
                        sx={{
                            position: 'relative',
                            px: 2,
                            py: 1,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            color: 'text.secondary',
                            fontWeight: isActive ? 600 : 400,
                            backgroundColor: isActive
                                ? alpha(tabColor, 0.12)
                                : 'transparent',
                            borderRadius: isActive ? '4px 4px 0 0' : 0,
                            transition: 'all 0.15s ease-in-out',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: isActive ? '100%' : '60%',
                                height: isActive ? '2px' : '1px',
                                backgroundColor: tabColor,
                            },
                            '&:hover': {
                                 backgroundColor: isActive
                                    ? alpha(tabColor, 0.12)
                                    : 'action.hover',
                                borderRadius: '4px 4px 0 0',
                            },
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 'inherit',
                                color: 'inherit',
                            }}
                        >
                            {tab.label}
                        </Typography>
                    </Box>
                );
            })}

            {canAddTab && (
                <Tooltip title="Add new tab">
                    <IconButton
                        onClick={onAddTab}
                        size="small"
                        aria-label="Add new tab"
                        sx={{ flexShrink: 0, ml: 0.5 }}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};